import type {
  QueryAcknowledgementDraft,
  QueryInitialAcknowledgement,
  QueryTimelineEvent,
  QueryWorkspace,
  QueuedEmailAcknowledgement,
  ResourceListResponse,
  ResourceResponse,
} from '@travel-crm/types';

import { apiRequest } from './api-client';

export const queryWorkspaceKeys = {
  root: ['query-workspace'] as const,
  workspace: (queryId: string) =>
    ['query-workspace', 'workspace', queryId] as const,
  initialAcknowledgement: (queryId: string) =>
    ['query-workspace', 'initial-acknowledgement', queryId] as const,
  acknowledgementDraft: (queryId: string, version?: number) =>
    ['query-workspace', 'acknowledgement-draft', queryId, version] as const,
  timeline: (
    queryId: string,
    cursor?: string,
    limit?: number,
  ) => ['query-workspace', 'timeline', queryId, cursor, limit] as const,
};

export async function fetchQueryWorkspace(
  queryId: string,
): Promise<QueryWorkspace> {
  return (
    await apiRequest<ResourceResponse<QueryWorkspace>>(
      `/api/v1/queries/${encodeURIComponent(queryId)}/workspace`,
    )
  ).data;
}

export async function fetchQueryInitialAcknowledgement(
  queryId: string,
): Promise<QueryInitialAcknowledgement | null> {
  return (
    await apiRequest<ResourceResponse<QueryInitialAcknowledgement | null>>(
      `/api/v1/queries/${encodeURIComponent(queryId)}/initial-acknowledgement`,
    )
  ).data;
}

export interface AcknowledgeQueryInput {
  expectedVersion: number;
  subject: string;
  bodyText: string;
  templateId: string;
}

export async function fetchManualQueryAcknowledgementDraft(
  queryId: string,
): Promise<QueryAcknowledgementDraft> {
  return (
    await apiRequest<ResourceResponse<QueryAcknowledgementDraft>>(
      `/api/v1/queries/${encodeURIComponent(queryId)}/initial-acknowledgement/draft`,
    )
  ).data;
}

export async function queueManualQueryAcknowledgement(
  queryId: string,
  input: AcknowledgeQueryInput,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<QueuedEmailAcknowledgement> {
  return (
    await apiRequest<ResourceResponse<QueuedEmailAcknowledgement>>(
      `/api/v1/queries/${encodeURIComponent(queryId)}/initial-acknowledgement`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify(input),
      },
    )
  ).data;
}

export async function retryManualQueryAcknowledgement(
  queryId: string,
  expectedVersion: number,
): Promise<QueuedEmailAcknowledgement> {
  return (
    await apiRequest<ResourceResponse<QueuedEmailAcknowledgement>>(
      `/api/v1/queries/${encodeURIComponent(queryId)}/initial-acknowledgement/retry`,
      {
        method: 'POST',
        body: JSON.stringify({ expectedVersion }),
      },
    )
  ).data;
}

export function fetchQueryTimeline(
  queryId: string,
  cursor?: string,
  limit?: number,
): Promise<ResourceListResponse<QueryTimelineEvent>> {
  const query = new URLSearchParams();
  if (cursor !== undefined && cursor !== '') query.set('cursor', cursor);
  if (limit !== undefined) query.set('limit', String(limit));
  const queryString = query.toString();

  return apiRequest<ResourceListResponse<QueryTimelineEvent>>(
    `/api/v1/queries/${encodeURIComponent(queryId)}/timeline${
      queryString === '' ? '' : `?${queryString}`
    }`,
  );
}
